'use strict';

/*
  after we finish integration with buy_button_form, we really should
  remove the "dealOptions" field from this code and instead use
  "multiOption" that is provided by buyButtonForm obj.
*/

const { h, Component } = require('phy');

const {
  isTraitAvailable,
  isPartOfCurrentSelection,
} = require('../util/trait_available');
const { getOptionBhcTag, getTraitBhwTag } = require('../util/bloodhound');
const getCurrentOptionsData = require('../util/map_options_data');
const formatOptionText = require('../util/format_option_text');
const getNotEmptySelectedTraits = require('../util/get_non_empty_traits');
const getCheapestOptionFromSelectedTraits = require('../util/get_cheapest_option');
const TraitsError = require('./TraitsError');
const TraitOption = require('./TraitOption');

const reconstructTraitOptionsMap = (dealOptions, traitOptionsMap) => {
  const traitsOptions = {};
  Object.keys(traitOptionsMap).forEach(key => {
    traitsOptions[key] = traitOptionsMap[key].map(optionUuid =>
      dealOptions.find(option => option.uuid === optionUuid)
    );
  });
  return traitsOptions;
};

class Traits extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedTraits: this.initTraitsState(),
      showError: false,
      traits: props.traits,
    };
    this.traitsOptions = reconstructTraitOptionsMap(
      props.dealOptions,
      props.traitOptionsMap
    );
    this.showVariationsError = this.showVariationsError.bind(this);
    this.updateFromCarousel = this.updateFromCarousel.bind(this);
  }

  componentDidMount() {
    window.DealEventPipe.on('carouselUpdate', this.updateFromCarousel);
    if (window.BuyButtonEventPipe) {
      window.BuyButtonEventPipe.on('buy_button_clicked', () => {
        const allTraitsSelected =
          Object.keys(getNotEmptySelectedTraits(this.state.selectedTraits))
            .length === this.state.traits.length;
        if (!allTraitsSelected) {
          this.showVariationsError();
          return { preventCheckout: true };
        }
        return { optionData: this.state.currentOption };
      });
    }
    window.DealEventPipe.on('showVariationsError', this.showVariationsError);

    window.addEventListener(
      'load',
      () => {
        // if there is a preselected option trigger a simulated first selection
        const { preSelectedTraits } = this.props;
        if (preSelectedTraits) {
          const firstOption = preSelectedTraits[0];
          this.handleTraitSelection({
            trait: firstOption.name,
            option: firstOption.value,
          });
        } else {
          // if browser input cache tries to select an element
          // update purchase cluster to match; cycle through each select element, check selected value, update traits
          [
            ...document.querySelectorAll('#variation-selector-widget select'),
          ].forEach(({ name: trait, value: option }) => {
            if (option && option.length > 0) {
              this.handleTraitSelection({ trait, option });
            }
          });
        }
      },
      { once: true }
    );
  }

  initTraitsState() {
    const { traits, preSelectedTraits } = this.props;
    const traitsState = {};
    traits.forEach(trait => {
      traitsState[trait.name] = '';
    });
    if (preSelectedTraits) {
      preSelectedTraits.forEach(trait => {
        traitsState[trait.name] = trait.value;
      });
    }
    return traitsState;
  }

  showVariationsError() {
    this.setState({
      showError: true,
    });
  }

  findCurrentStateTraitValue(trait, value) {
    const stateTrait = this.state.traits.find(
      currentTrait => currentTrait.name === trait
    );
    return stateTrait.values.find(currentValue => currentValue.value === value);
  }

  updateFromCarousel(carouselData) {
    for (const trait in carouselData) {
      if (carouselData[trait].length === 1) {
        const value = carouselData[trait][0];
        // To avoid selecting a sold out option from the carousel
        let traitValue = this.findCurrentStateTraitValue(trait, value);
        if (traitValue.isAvailable) {
          this.handleTraitSelection({
            trait,
            option: value,
          });
        } else if (this.props.clearTraitsOnCarouselSelection) {
          // if current options are not available reset and select only the ones available
          this.setState({
            selectedTraits: this.initTraitsState(),
            traits: this.props.traits,
          });
          traitValue = this.findCurrentStateTraitValue(trait, value);
          if (traitValue.isAvailable) {
            this.handleTraitSelection({
              trait,
              option: value,
            });
          }
        }
      }
    }
  }

  getOptionsIntersectionOfSelectedTraits(notEmptySelectedTraits) {
    return Object.keys(notEmptySelectedTraits).reduce(
      (selectedTraitsOptionsIntersection, selectedTraitKey) => {
        const keyValue = notEmptySelectedTraits[selectedTraitKey];
        const selectedOptions = this.traitsOptions[
          `${selectedTraitKey}-${keyValue}`
        ];
        if (selectedOptions) {
          if (selectedTraitsOptionsIntersection.length) {
            // eslint-disable-next-line no-param-reassign
            selectedTraitsOptionsIntersection = selectedTraitsOptionsIntersection.filter(
              current =>
                selectedOptions.some(option => option.uuid === current.uuid)
            );
          } else {
            // eslint-disable-next-line no-param-reassign
            selectedTraitsOptionsIntersection = selectedOptions;
          }
        }
        return selectedTraitsOptionsIntersection;
      },
      this.props.dealOptions
    );
  }

  filterAvailableTraits(notEmptySelectedTraits, lastTraitSelected) {
    return this.state.traits.map(trait => {
      const { name, values } = trait;
      let traitValues = values;
      if (!notEmptySelectedTraits[name]) {
        const optionsAvailable = this.getOptionsIntersectionOfSelectedTraits(
          notEmptySelectedTraits
        );
        traitValues = values.map(value => ({
          value: value.value,
          options: value.options,
          isAvailable: isTraitAvailable(value.options, optionsAvailable),
          isCurrentSelection: isPartOfCurrentSelection(
            value.options,
            optionsAvailable
          ),
        }));
      } else if (lastTraitSelected !== name) {
        //create option availability based on other trait selections
        const otherTraitSelections = getNotEmptySelectedTraits(
          notEmptySelectedTraits,
          name
        );
        const selectedTraitsOptions = this.getOptionsIntersectionOfSelectedTraits(
          otherTraitSelections
        );
        traitValues = values.map(value => ({
          value: value.value,
          options: value.options,
          isAvailable: isTraitAvailable(value.options, selectedTraitsOptions),
          isCurrentSelection: isPartOfCurrentSelection(
            value.options,
            selectedTraitsOptions
          ),
        }));
      }
      return { name, values: traitValues };
    });
  }

  handleTraitSelection({ trait, option }) {
    const newSelectedTraits = { ...this.state.selectedTraits };
    newSelectedTraits[trait] = option;
    const notEmptySelectedTraits = getNotEmptySelectedTraits(newSelectedTraits);
    const traitsSelected = Object.keys(notEmptySelectedTraits).length;
    const allTraitsSelected = traitsSelected === this.state.traits.length;
    let newAvailableTraits = this.props.traits;
    if (traitsSelected) {
      newAvailableTraits = this.filterAvailableTraits(
        notEmptySelectedTraits,
        trait
      );
    }
    const currentOption = getCheapestOptionFromSelectedTraits(
      notEmptySelectedTraits,
      this.props.dealOptions,
      this.traitsOptions
    );

    const sharedData = getCurrentOptionsData(currentOption, allTraitsSelected);
    sharedData.dealUuid = this.props.dealUuid;

    // stop showing error after all traits are selected
    const showError = this.state.showError ? !allTraitsSelected : false;

    // delivery estimates depends on this global variable for initiation
    window.VariationsWidget.currentOptionInfo = sharedData;

    window.DealEventPipe.trigger('traitsUpdate', {
      selectedTraits: notEmptySelectedTraits,
    });
    window.DealEventPipe.trigger('priceUpdate', sharedData);
    window.DealEventPipe.trigger('goodsOptionUpdate', sharedData);

    if (window.BuyButtonEventPipe) {
      const { buyUrl, dealUuid, uuid } = sharedData;
      window.BuyButtonEventPipe.trigger('option_updated', {
        allTraitsSelected,
        buyUrl,
        dealUuid,
        isTraits: true,
        optionUuid: uuid,
      });
    }

    this.setState({
      currentOption,
      selectedTraits: newSelectedTraits,
      showError,
      traits: newAvailableTraits,
      lastSelectedTrait: trait,
    });
  }

  render() {
    const I18n = this.props.I18n || window.I18n;
    const { traits, selectedTraits, showError } = this.state;
    const { pageType, traitsErrorMsg, printSoldOut } = this.props;
    return h(
      'div',
      {
        'data-bhw': 'VariationsWidget',
        id: 'variation-selector-widget',
      },
      h(TraitsError, { showError, traitsErrorMsg }),
      traits.map((trait, traitIndex) => {
        const { name, values } = trait;
        const selectText = I18n.t('js.modules.variations.select', { name });
        const errorClass =
          showError && selectedTraits[name] === '' ? 'error' : '';
        return h(
          'select',
          {
            'aria-label': selectText,
            autocomplete: 'off',
            className: `v2-dropdown-input variation-trait ${errorClass}`,
            'data-bhw': getTraitBhwTag(name),
            id: `trait-${traitIndex}`,
            key: `trait-${traitIndex}`,
            name,
            onChange: ({ target }) =>
              this.handleTraitSelection({
                trait: target.name,
                option: target.value,
              }),
            required: true,
            title: selectText,
          },
          h(TraitOption, {
            bhc: getOptionBhcTag(selectText),
            optionId: 'select',
            optionText: selectText,
            selected: selectedTraits[name] === '' ? 'selected' : null,
            traitId: traitIndex,
            value: '',
          }),
          values.map((element, optionIndex) => {
            const { value, isAvailable, isCurrentSelection } = element;
            const optionText =
              printSoldOut && !isAvailable
                ? `${I18n.t('js.modules.variations.sold_out')} ${value}`
                : value;
            const selected = selectedTraits[name] === value ? 'selected' : null;
            const forceDisplayClass =
              printSoldOut && !isAvailable && isCurrentSelection
                ? 'force-display'
                : '';
            const optionTextWithFromPrice = formatOptionText(
              pageType,
              selected,
              optionText,
              name,
              value,
              selectedTraits,
              this.props.dealOptions,
              this.traitsOptions,
              I18n
            );
            return h(TraitOption, {
              className: forceDisplayClass,
              disabled: !isAvailable,
              optionId: optionIndex,
              optionText: optionTextWithFromPrice,
              selected,
              traitId: traitIndex,
              value,
            });
          })
        );
      })
    );
  }
}

module.exports = Traits;
